import { DEMO_CAMPAIGN_ID, DEMO_TEAM_CAMPAIGN_ID } from "@playlive/fundraiser-data/demo";
import playLiveOpenGraph from "@/assets/playlive-og-image-preview.jpg";
import { getAssetImageUrl } from "@/util/common";

export const isTestMode = import.meta.env.VITE_APP_ENV === "qa";

/**
 * Campaign IDs that exist only as QA/demo fixtures.
 *
 * `defaultTiltifyConfig` seeds `tiltifyCampaignID` with
 * {@link DEMO_CAMPAIGN_ID} whenever {@link isTestMode} is on, so on QA a
 * signed-in user who hasn't picked a campaign yet is carrying a truthy
 * but entirely fictional ID. Tiltify 404s on it, so any feature that
 * would otherwise call a campaign-scoped API needs to recognize it and
 * serve fixtures instead.
 */
const DEMO_CAMPAIGN_IDS: ReadonlySet<string> = new Set([DEMO_CAMPAIGN_ID, DEMO_TEAM_CAMPAIGN_ID]);

/** Whether `campaignID` refers to a demo fixture campaign. */
export const isDemoCampaignID = (campaignID: string | null | undefined): boolean =>
  campaignID !== null && campaignID !== undefined && DEMO_CAMPAIGN_IDS.has(campaignID);

/**
 * Detects whether the app is running in a developer environment.
 * Mirrors the logic used by `DevModal`/`DevCustomizeOptions`:
 *   - `foo.dev.*` / `foo.qa.*` hosts → env comes from the subdomain
 *   - `foo.experience.*` hosts → prod
 *   - Single-segment hosts (e.g. `localhost`) → falls back to `VITE_APP_ENV`
 */
export function getRuntimeEnvironment(): "dev" | "qa" | "prod" {
  if (typeof window === "undefined") {
    return (import.meta.env.VITE_APP_ENV as "dev" | "qa" | "prod" | undefined) ?? "prod";
  }

  const splitHost = window.location.host.split(".");
  if (splitHost.length > 1) {
    const hostEnvironment = splitHost[1];
    if (hostEnvironment === "dev" || hostEnvironment === "qa") {
      return hostEnvironment;
    }
    if (hostEnvironment === "experience") {
      return "prod";
    }
  }

  return (import.meta.env.VITE_APP_ENV as "dev" | "qa" | "prod" | undefined) ?? "prod";
}

export const isDevMode = getRuntimeEnvironment() === "dev";

export const isTwitchEnabled =
  !import.meta.env.VITE_TWITCH_DISABLED || import.meta.env.VITE_TWITCH_DISABLED !== "true";

export const CHARITY = {
  TILTIFY: "tiltify",
  TWITCH: "twitch",
};

export const siteDescription = isTestMode
  ? `Keep everyone up-to-date on your fundraising progress, use themed graphics on your stream, connect with chat through new interactive chatbot commands, and easily share facts.`
  : `Keep everyone up-to-date on your fundraising progress, use themed graphics on your stream, connect with chat through new interactive chatbot commands, and easily share facts about the life-saving mission of St. Jude Children's Research Hospital.`;

export const defaultOpenGraphImage = getAssetImageUrl(playLiveOpenGraph);
